import { FC, HTMLAttributes, useRef, FocusEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { IconButton } from '@legacy/components/mui-components';
import { Search as MuiSearch } from '@mui/icons-material';
import { useSearchKeyboardEvent } from './MainSearchHookHelper';
import Search from './components/Search';
import styles from './MainSearch.module.scss';
import { useSearchStore } from '@/stores/search';

interface MainSearchProps extends HTMLAttributes<HTMLButtonElement> {}

export const MainSearch: FC<MainSearchProps> = () => {
  const MainSearchRef = useRef<HTMLDivElement>(null);
  const MainSearchTriggerRef = useRef<HTMLButtonElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const searchFilterRef = useRef<HTMLDivElement>(null);
  const [selectItemIndex, setSelectItemIndex] = useState<number>(-1);
  const [searchValue, setSearchValue] = useState<string>('');

  const { isMainSearchOpen, updateStore } = useSearchStore();

  const closeSearch = () => {
    setSelectItemIndex(-1);
    setSearchValue('');
    updateStore({
      type: 'RESET_SEARCH',
    });
  };

  const toggleSearch = () =>
    updateStore({
      type: 'TOGGLE_SEARCH',
    });

  const handleChange = () => {
    setTimeout(() => {
      searchInputRef?.current?.focus();
    }, 10);

    return toggleSearch();
  };

  const onBlurHandle = ({
    target,
    currentTarget,
    relatedTarget,
  }: FocusEvent<HTMLDivElement>) => {
    const isSearchIconButton = MainSearchTriggerRef?.current === relatedTarget;
    const isNotSelfTrigger = currentTarget !== target;
    const isNotChildEvent = !currentTarget?.contains(relatedTarget as Node);

    if (isSearchIconButton) {
      return;
    }

    if (isNotSelfTrigger && isNotChildEvent) {
      closeSearch();
    } else {
      const relatedTargetId = (relatedTarget as any)?.getAttribute(
        'data-automation-id',
      );

      if (relatedTargetId === 'HeaderSearchContainer') {
        searchInputRef?.current?.focus();
      } else if (relatedTargetId === 'MainSearchCategoryPanel') {
        searchFilterRef?.current?.focus();
      }
    }
  };

  useSearchKeyboardEvent(
    isMainSearchOpen,
    closeSearch,
    MainSearchRef.current,
    MainSearchTriggerRef,
  );

  const { t } = useTranslation('header');

  return (
    <div className={styles.Wrapper} ref={MainSearchRef}>
      <IconButton
        ref={MainSearchTriggerRef}
        aria-controls="main-search"
        aria-expanded={isMainSearchOpen}
        onClick={handleChange}
        data-automation-id="HeaderSearchButton"
        title={t('SearchButton')}
      >
        <MuiSearch />
      </IconButton>
      <div
        id="main-search"
        className={styles.PopOut}
        hidden={!isMainSearchOpen}
        tabIndex={-1}
        onBlur={onBlurHandle}
        data-automation-id="HeaderSearchContainer"
      >
        <Search
          inputRef={searchInputRef}
          searchFilterRef={searchFilterRef}
          selectItemIndex={selectItemIndex}
          setSelectItemIndex={setSelectItemIndex}
          searchValue={searchValue}
          setSearchValue={setSearchValue}
        />
      </div>
    </div>
  );
};
